import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { cn } from '../../lib/cn.js';
import { useTranslate } from '../../lib/i18n.js';
/**
 * The glyph and the colours per status. The *word* is not here: it comes from
 * `badge.<status>` in the dictionary, keyed by the same name, so a status is
 * one entry in this object and one line in each translation file.
 */
const BADGE_CONFIG = {
    published: {
        glyph: '●',
        classes: 'bg-accent-light border-accent-border text-success-text',
    },
    draft: {
        glyph: '○',
        classes: 'bg-surface-secondary border-border-strong text-text-strong',
    },
    assigned: {
        glyph: '▶',
        classes: 'bg-primary-light border-primary-border text-primary-dark',
    },
    error: {
        glyph: '⚠',
        classes: 'bg-error-light border-error-border text-error-text',
    },
    // Retired, not broken: the muted treatment says "put away" where `error`
    // would say "something went wrong".
    archived: {
        glyph: '◍',
        classes: 'bg-surface-tertiary border-border-strong text-text-secondary',
    },
    // Held back by the tutor's plan rather than put away by the tutor. Muted
    // like `archived`, because nothing is wrong, but a dashed edge and its own
    // glyph so the two are never mistaken: one comes back when there is room,
    // the other only when the tutor restores it.
    setAside: {
        glyph: '◇',
        classes: 'bg-surface-tertiary border-dashed border-border-strong text-text-secondary',
    },
    /* Why the dashboard's attention statuses live here too ─────────────────
     *
     * These four say something about a student and a practice together rather
     * than about a practice on its own, so they are a second vocabulary in one
     * config. They are here because the dashboard had already built its own
     * pill from these exact four colour pairs — borrowing the contrast, which
     * had been settled once, but not the component. The result was two pill
     * sizes on one screen and, more to the point, the only four statuses in the
     * product carried by colour and word with no glyph.
     *
     * The colours are deliberately the same pairs as above. A tutor is not
     * meant to read "grey means low score"; they are meant to read the word.
     * The glyphs come from the same Unicode block as the ones above, so they
     * render from the same fonts.
     */
    // Urgent, not wrong: amber, the warning family, with the -text value that
    // passes as type. Red is kept for an error and for what cannot be undone.
    closing: {
        // A clock face running down, for a practice whose date is nearly up.
        glyph: '◷',
        classes: 'bg-warning/5 border-warning/40 text-warning-text',
    },
    lowScore: {
        glyph: '▼',
        classes: 'bg-surface-secondary border-border-strong text-text-strong',
    },
    inProgress: {
        // Half filled: begun and not finished.
        glyph: '◐',
        classes: 'bg-primary-light border-primary-border text-primary-dark',
    },
    notStarted: {
        // An outline rather than the hollow circle `draft` uses, so the two do
        // not read as the same state in two places.
        glyph: '◌',
        classes: 'bg-surface-tertiary border-border-strong text-text-secondary',
    },
};
/**
 * Every status there is, in declaration order.
 *
 * Derived from the config rather than written out again, so a status added
 * above is covered by the story and the test below without anyone remembering
 * to list it in two more places — and "colour never carries meaning alone"
 * stays a rule the suite enforces rather than a habit.
 */
export const BADGE_STATUSES = Object.keys(BADGE_CONFIG);
/**
 * Badge (README.md > Atoms > "Badge (status)"). Colour never carries the
 * status alone — every badge pairs its colour with a glyph and a word.
 */
export function Badge({ status, children, className }) {
    const t = useTranslate();
    const config = BADGE_CONFIG[status];
    return (_jsxs("span", { className: cn('inline-flex items-center gap-1.5 rounded-full border px-[18px] py-2 text-label font-semibold', config.classes, className), children: [_jsx("span", { "aria-hidden": "true", children: config.glyph }), _jsx("span", { children: children ?? t(`badge.${status}`) })] }));
}
//# sourceMappingURL=Badge.js.map